import axios from 'axios'

export interface DeepSeekMessage {
  role: 'system' | 'user' | 'assistant'
  content: string
}

export interface DeepSeekResponse {
  id: string
  object: string
  created: number
  model: string
  choices: Array<{
    index: number
    message: {
      role: 'assistant'
      content: string
    }
    finish_reason: string
  }>
  usage: {
    prompt_tokens: number
    completion_tokens: number
    total_tokens: number
  }
}

export interface DeepSeekConfig {
  apiKey?: string
  baseUrl?: string
  model?: string
  temperature?: number
  maxTokens?: number
  topP?: number
  frequencyPenalty?: number
  presencePenalty?: number
}

class DeepSeekService {
  private apiKey: string
  private baseUrl: string
  private model: string
  private temperature: number
  private maxTokens: number
  private topP: number
  private frequencyPenalty: number
  private presencePenalty: number
  private apiConfigured: boolean

  isConfigured(): boolean {
    return this.apiConfigured
  }

  constructor(config: DeepSeekConfig = {}) {
    this.apiKey = config.apiKey || import.meta.env.VITE_DEEPSEEK_API_KEY || 'sk-xxxxxxxx'
    this.baseUrl = config.baseUrl || import.meta.env.VITE_DEEPSEEK_API_URL || 'https://api.deepseek.com'
    this.model = config.model || 'deepseek-chat'
    this.temperature = config.temperature ?? 0.7
    this.maxTokens = config.maxTokens ?? 2048
    this.topP = config.topP ?? 0.95
    this.frequencyPenalty = config.frequencyPenalty ?? 0
    this.presencePenalty = config.presencePenalty ?? 0
    this.apiConfigured = !!(this.apiKey && this.apiKey !== 'sk-xxxxxxxx' && this.apiKey !== 'your-api-key-here')
  }

  private async request<T>(endpoint: string, data: Record<string, any>): Promise<T> {
    try {
      const response = await axios.post(`${this.baseUrl}${endpoint}`, data, {
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${this.apiKey}`
        },
        timeout: 60000
      })
      return response.data
    } catch (error) {
      console.error('DeepSeek API request failed:', error)
      throw error
    }
  }

  async chat(messages: DeepSeekMessage[], options?: {
    temperature?: number
    maxTokens?: number
    topP?: number
    frequencyPenalty?: number
    presencePenalty?: number
  }): Promise<DeepSeekResponse> {
    if (!this.isConfigured()) {
      return {
        id: 'empty_' + Date.now(),
        object: 'chat.completion',
        created: Date.now(),
        model: 'local-fallback',
        choices: [{ index: 0, message: { role: 'assistant', content: '' }, finish_reason: 'stop' }],
        usage: { prompt_tokens: 0, completion_tokens: 0, total_tokens: 0 }
      }
    }
    const data = {
      model: this.model,
      messages,
      temperature: options?.temperature ?? this.temperature,
      max_tokens: options?.maxTokens ?? this.maxTokens,
      top_p: options?.topP ?? this.topP,
      frequency_penalty: options?.frequencyPenalty ?? this.frequencyPenalty,
      presence_penalty: options?.presencePenalty ?? this.presencePenalty,
      stream: false
    }
    return this.request<DeepSeekResponse>('/chat/completions', data)
  }

  async streamChat(messages: DeepSeekMessage[], onChunk: (content: string) => void, options?: {
    temperature?: number
    maxTokens?: number
  }): Promise<void> {
    const data = {
      model: this.model,
      messages,
      temperature: options?.temperature ?? this.temperature,
      max_tokens: options?.maxTokens ?? this.maxTokens,
      stream: true
    }

    try {
      const response = await axios.post(`${this.baseUrl}/chat/completions`, data, {
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${this.apiKey}`
        },
        timeout: 120000,
        responseType: 'stream'
      })

      const reader = response.data.getReader()
      const decoder = new TextDecoder('utf-8')

      while (true) {
        const { done, value } = await reader.read()
        if (done) break

        const chunk = decoder.decode(value)
        const lines = chunk.split('\n').filter(line => line.trim() !== '')

        for (const line of lines) {
          if (line.startsWith('data: ')) {
            const jsonStr = line.slice(6)
            if (jsonStr === '[DONE]') break
            
            try {
              const json = JSON.parse(jsonStr)
              const content = json.choices?.[0]?.delta?.content
              if (content) {
                onChunk(content)
              }
            } catch (e) {
              console.warn('Failed to parse stream chunk:', e)
            }
          }
        }
      }
    } catch (error) {
      console.error('DeepSeek stream request failed:', error)
      throw error
    }
  }

  async generateText(prompt: string, options?: {
    temperature?: number
    maxTokens?: number
  }): Promise<string> {
    const messages: DeepSeekMessage[] = [
      {
        role: 'system',
        content: '你是谐伴AI助手，一个专业的后台管理系统智能助手。请使用中文进行回复，保持友好、专业的语气。'
      },
      {
        role: 'user',
        content: prompt
      }
    ]

    const response = await this.chat(messages, options)
    return response.choices[0]?.message?.content || ''
  }

  async analyzeData(data: any, prompt: string): Promise<string> {
    const messages: DeepSeekMessage[] = [
      {
        role: 'system',
        content: '你是一位数据分析专家，请基于提供的数据进行深入分析。使用中文回复，提供详细的分析报告和建议。'
      },
      {
        role: 'user',
        content: `数据：\n${JSON.stringify(data, null, 2)}\n\n分析要求：${prompt}`
      }
    ]

    const response = await this.chat(messages)
    return response.choices[0]?.message?.content || ''
  }

  async summarizeText(text: string, options?: {
    maxLength?: number
  }): Promise<string> {
    const maxLength = options?.maxLength || 500
    const messages: DeepSeekMessage[] = [
      {
        role: 'system',
        content: '你是一位专业的文本摘要专家。请将提供的文本进行简明扼要的总结，保持核心信息完整，控制在指定长度内。'
      },
      {
        role: 'user',
        content: `请总结以下文本（最多${maxLength}字）：\n${text}`
      }
    ]

    const response = await this.chat(messages)
    return response.choices[0]?.message?.content || ''
  }

  async extractKeywords(text: string, count: number = 10): Promise<string[]> {
    const messages: DeepSeekMessage[] = [
      {
        role: 'system',
        content: '你是一位关键词提取专家。请从提供的文本中提取最重要的关键词，返回一个JSON数组。'
      },
      {
        role: 'user',
        content: `请从以下文本中提取${count}个最重要的关键词：\n${text}\n\n请直接返回JSON数组，不要包含其他内容。`
      }
    ]

    const response = await this.chat(messages)
    const content = response.choices[0]?.message?.content || '[]'
    
    try {
      return JSON.parse(content)
    } catch {
      return content.split(/[，,、\s]+/).filter(k => k.trim())
    }
  }

  async classifyText(text: string, categories: string[]): Promise<string> {
    const messages: DeepSeekMessage[] = [
      {
        role: 'system',
        content: '你是一位文本分类专家。请根据提供的分类列表，将文本分类到最合适的类别中。'
      },
      {
        role: 'user',
        content: `文本：${text}\n\n分类列表：${categories.join('、')}\n\n请直接返回分类结果，不要包含其他内容。`
      }
    ]

    const response = await this.chat(messages)
    return response.choices[0]?.message?.content || ''
  }

  async translateText(text: string, targetLang: string = 'zh'): Promise<string> {
    const messages: DeepSeekMessage[] = [
      {
        role: 'system',
        content: '你是一位专业翻译专家。请将提供的文本翻译成目标语言，保持原意准确。'
      },
      {
        role: 'user',
        content: `请将以下文本翻译成${targetLang === 'zh' ? '中文' : '英文'}：\n${text}`
      }
    ]

    const response = await this.chat(messages)
    return response.choices[0]?.message?.content || ''
  }

  async generateReport(data: any, type: string = 'summary'): Promise<string> {
    const templates: Record<string, string> = {
      summary: '请对以下数据进行综合分析，生成一份详细的总结报告。',
      sales: '请对以下销售数据进行分析，包括趋势、对比、异常点和建议。',
      user: '请对以下用户数据进行分析，包括用户画像、行为特征和增长趋势。',
      performance: '请对以下性能数据进行分析，包括指标评估、瓶颈识别和优化建议。',
      security: '请对以下安全数据进行分析，包括风险评估、漏洞识别和改进建议。'
    }

    const template = templates[type] || templates.summary

    const messages: DeepSeekMessage[] = [
      {
        role: 'system',
        content: '你是一位专业的报告生成专家。请基于提供的数据生成一份结构化的报告，包括分析、结论和建议。'
      },
      {
        role: 'user',
        content: `数据：\n${JSON.stringify(data, null, 2)}\n\n${template}`
      }
    ]

    const response = await this.chat(messages)
    return response.choices[0]?.message?.content || ''
  }

  async getHealthStatus(): Promise<{ status: 'online' | 'offline' | 'degraded'; latency: number }> {
    const startTime = Date.now()
    try {
      const messages: DeepSeekMessage[] = [
        { role: 'user', content: 'ping' }
      ]
      await this.chat(messages, { maxTokens: 10 })
      const latency = Date.now() - startTime
      return { status: 'online', latency }
    } catch {
      return { status: 'offline', latency: 0 }
    }
  }
}

export const deepseekService = new DeepSeekService()

export { DeepSeekService }